import { useRef } from 'preact/hooks';

import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { usePress } from '../hooks/UNSAFE_usePress';
import { useToggle } from '../hooks/UNSAFE_useToggle';
import { BundleType } from '../resources/nls/bundle';

import { HelpIcon } from '../PRIVATE_ThemedIcons/HelpIcon';
import { Popup } from '../UNSAFE_Popup';
import { InlineHelp } from './InlineHelp';
import {
  iconUserAssistanceIcon,
  iconUserAssistancePopupFocus
} from './themes/UserAssistanceStyles.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { classNames } from '../utils/UNSAFE_classNames';
import { PopupRedwoodTheme } from '../UNSAFE_Popup/themes/redwood/PopupTheme';

type Props = {
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: string;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: string;
  /**
   * Custom text to be rendered for the `helpSourceLink`. "Learn more" will be used if no custom text is provided.
   */
  helpSourceText?: string;
  /**
   * Whether the trigger element is in tab sequence
   */
  isTabbable?: boolean;
  /**
   * ID of the pop up content that can be used for aria-describedby
   */
  id?: string;
};

/**
 * Created to match icon user assistance spec for use with radio option, pending design review
 * of inline UA assistance.
 */
const IconUserAssistance = ({
  assistiveText,
  helpSourceLink,
  helpSourceText,
  isTabbable = true,
  id
}: Props) => {
  const { bool: isOpen, setFalse: setIsOpenFalse, setTrue: setIsOpenTrue } = useToggle(false);

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const userAssistance_learnMoreStr = translations.userAssistance_learnMore();
  const sourceText = helpSourceLink && (helpSourceText || userAssistance_learnMoreStr);
  // We are referencing popup vars, so we need to bring in the baseTheme for popup
  const { baseTheme: popupBaseTheme } = useComponentTheme(PopupRedwoodTheme);

  const { pressProps } = usePress((e) => {
    e.preventDefault();
    setIsOpenTrue();
  });
  const anchorRef = useRef(null);

  const iconUserAssistanceClasses = classNames([popupBaseTheme, iconUserAssistancePopupFocus]);

  return helpSourceLink || assistiveText ? (
    <>
      <span
        {...pressProps}
        ref={anchorRef}
        tabIndex={isTabbable ? 0 : -1}
        class={iconUserAssistanceIcon}
        id={`${id}-help-icon`}>
        <HelpIcon />
      </span>
      <Popup
        placement="top"
        isOpen={isOpen}
        anchorRef={anchorRef}
        onClose={setIsOpenFalse}
        onClickOutside={setIsOpenFalse}
        shiftOptions={{ mainAxis: true, crossAxis: false }}
        initialFocus="firstFocusable">
        <div id={id} tabIndex={0} class={iconUserAssistanceClasses}>
          <InlineHelp
            assistiveText={assistiveText}
            sourceLink={helpSourceLink}
            sourceText={sourceText}
          />
        </div>
      </Popup>
    </>
  ) : null;
};

export { IconUserAssistance };
